import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Global "we couldn't process your file" dialog state.
 *
 * Image uploads across every flow funnel through `useImageUpload`
 * (src/hooks/useImageUpload.ts). When HEIC→JPEG conversion (or any
 * pre-upload normalization) fails, that hook calls `open()` here instead
 * of silently aborting. The dialog itself is mounted exactly once at the
 * app root (UploadErrorDialog), so guiding the user to a supported format
 * is consistent everywhere and never copy-pasted per flow.
 *
 * Mirrors the FeatureRequestDialogContext shape (provider + hook) so the
 * contributor mental model is identical.
 */

interface UploadErrorDialogContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const UploadErrorDialogContext = createContext<
  UploadErrorDialogContextValue | undefined
>(undefined);

export function UploadErrorDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return (
    <UploadErrorDialogContext.Provider value={value}>
      {children}
    </UploadErrorDialogContext.Provider>
  );
}

export function useUploadErrorDialog(): UploadErrorDialogContextValue {
  const ctx = useContext(UploadErrorDialogContext);
  if (!ctx) {
    throw new Error(
      "useUploadErrorDialog must be used within an UploadErrorDialogProvider",
    );
  }
  return ctx;
}
